<template>
  <div class="musubi-page musubi-page--subscribers">
    <header class="musubi-page__head">
      <h1>Subscribers</h1>
      <p class="text-muted">
        Everyone Yorlin can email. Status is the global delivery state —
        unsubscribes, bounces and complaints are honoured automatically and every
        send is gated against opt-out. Per-list membership is managed below.
      </p>
    </header>

    <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>

    <section class="actions-bar">
      <button class="btn btn-outline-primary" @click="openCreate">
        <CygnusIcon name="plus" class="me-1" />New subscriber
      </button>
      <button class="btn btn-link" :disabled="busy.load" @click="refresh">
        <CygnusIcon name="spinner" :spin="true" v-if="busy.load" />
        <span v-else><CygnusIcon name="refresh" /> Refresh</span>
      </button>
    </section>

    <!-- Editor (create OR edit existing) -->
    <section v-if="editor" class="editor card mb-3">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-start mb-3">
          <h6 class="mb-0">{{ editor.id ? `Edit subscriber #${editor.id}` : 'New subscriber' }}</h6>
          <button class="btn btn-link btn-sm" @click="closeEditor">Close</button>
        </div>

        <div v-if="isErased" class="alert alert-warning py-2 small">
          This subscriber was erased (GDPR right-to-erasure) on {{ formatDate(editor.erased_at) }}. The record is anonymised and read-only.
        </div>

        <form @submit.prevent="saveSubscriber">
          <div class="row g-2 mb-2">
            <div class="col-md-6">
              <label class="form-label small text-muted">Email *</label>
              <input v-model="editor.email" type="email" required class="form-control form-control-sm" placeholder="name@example.com" :disabled="isErased">
            </div>
            <div class="col-md-3">
              <label class="form-label small text-muted">Status</label>
              <select v-model="editor.status" class="form-select form-select-sm" :disabled="isErased">
                <option value="active">Active</option>
                <option value="pending">Pending (unconfirmed)</option>
                <option value="unsubscribed">Unsubscribed</option>
              </select>
            </div>
            <div class="col-md-3">
              <label class="form-label small text-muted">Source</label>
              <input v-model="editor.source" type="text" class="form-control form-control-sm" placeholder="manual" :disabled="isErased">
            </div>
          </div>

          <div class="row g-2 mb-2">
            <div class="col-md-4">
              <label class="form-label small text-muted">First name</label>
              <input v-model="editor.first_name" type="text" class="form-control form-control-sm" :disabled="isErased">
            </div>
            <div class="col-md-4">
              <label class="form-label small text-muted">Last name</label>
              <input v-model="editor.last_name" type="text" class="form-control form-control-sm" :disabled="isErased">
            </div>
            <div class="col-md-4">
              <label class="form-label small text-muted">Display name</label>
              <input v-model="editor.name" type="text" class="form-control form-control-sm" placeholder="defaults to first + last" :disabled="isErased">
            </div>
          </div>

          <!-- On create: say how this person came to be a contact (sent as basis) -->
          <div v-if="!editor.id" class="mb-2">
            <label class="form-label small text-muted">How this person became a contact</label>
            <select v-model="editor.basis" class="form-select form-select-sm">
              <option value="legitimate_interest">A business contact who has not signed up (legitimate interest)</option>
              <option value="consent">They signed up or asked to hear from you (consent)</option>
            </select>
            <div class="form-text small">
              A business contact is treated as cold email and follows your workspace's cold email setting: by default, campaigns,
              drips and one-off emails only reach them while their company is recorded as registered or public sector.
              Choose "signed up" only when this person asked to hear from you; the contact is then recorded as confirmed today.
            </div>
          </div>

          <!-- On create: optionally add to a list straight away -->
          <div v-if="!editor.id" class="mb-2">
            <label class="form-label small text-muted">Add to list (optional)</label>
            <select v-model.number="editor.list_id" class="form-select form-select-sm">
              <option :value="null">— none —</option>
              <option v-for="l in lists" :key="l.id" :value="l.id">{{ l.name }} (#{{ l.id }})</option>
            </select>
          </div>

          <div class="d-flex gap-2 mt-3">
            <button type="submit" class="btn btn-primary btn-sm" :disabled="busy.save || !editor.email || isErased">
              <CygnusIcon name="spinner" :spin="true" v-if="busy.save" />
              <span v-else>{{ editor.id ? 'Save changes' : 'Create subscriber' }}</span>
            </button>
            <button
              v-if="editor.id && !isErased && editor.status !== 'unsubscribed'"
              type="button"
              class="btn btn-outline-primary btn-sm"
              @click="composeToSubscriber"
            >
              <CygnusIcon name="envelope" class="me-1" />Send email
            </button>
            <button
              v-if="editor.id"
              type="button"
              class="btn btn-outline-danger btn-sm ms-auto"
              @click="deleteSubscriber(editor.id)"
            >
              <CygnusIcon name="trash" class="me-1" />Delete
            </button>
          </div>
        </form>

        <!-- Compliance (edit mode only) -->
        <div v-if="editor.id" class="compliance mt-4">
          <h6 class="small text-muted text-uppercase mb-2">Compliance</h6>
          <dl class="compliance__grid mb-0 small">
            <dt>Basis</dt>
            <dd>
              <span :class="['badge', editor.basis === 'legitimate_interest' ? 'bg-info text-dark' : 'bg-success']">
                {{ editor.basis === 'legitimate_interest' ? 'Legit. interest' : 'Consent' }}
              </span>
            </dd>
            <template v-if="editor.replied_at">
              <dt>Replied</dt>
              <dd>{{ formatDate(editor.replied_at) }}</dd>
            </template>
            <template v-if="editor.bounced_at">
              <dt>Bounced</dt>
              <dd>{{ formatDate(editor.bounced_at) }}</dd>
            </template>
            <template v-if="editor.complained_at">
              <dt>Complained</dt>
              <dd>{{ formatDate(editor.complained_at) }}</dd>
            </template>
            <template v-if="editor.erased_at">
              <dt>Erased</dt>
              <dd>{{ formatDate(editor.erased_at) }}</dd>
            </template>
          </dl>
        </div>

        <!-- Tags (edit mode only) -->
        <div v-if="editor.id" class="tags mt-4">
          <h6 class="small text-muted text-uppercase mb-2">Tags</h6>
          <div class="tag-chips mb-2">
            <span v-for="t in assignedTags" :key="t.id" class="tag-chip">
              {{ t.label }}
              <button type="button" class="tag-chip__x" :disabled="busy.tag" title="Remove tag" @click="removeTag(t.id)">&times;</button>
            </span>
            <span v-if="!assignedTags.length" class="small text-muted">No tags yet.</span>
          </div>
          <div class="d-flex gap-2 align-items-end">
            <div class="flex-grow-1">
              <label class="form-label small text-muted">Add a tag</label>
              <input
                v-model="tagInput"
                type="text"
                class="form-control form-control-sm"
                list="subscriber-tag-suggestions"
                placeholder="e.g. vip, webinar-2026 (creates new tags on the fly)"
                @keydown.enter.prevent="addTag"
              >
              <datalist id="subscriber-tag-suggestions">
                <option v-for="t in unassignedTags" :key="t.id" :value="t.label"></option>
              </datalist>
            </div>
            <button class="btn btn-outline-primary btn-sm" :disabled="!tagInput.trim() || busy.tag" @click="addTag">
              <CygnusIcon name="spinner" :spin="true" v-if="busy.tag" />
              <span v-else><CygnusIcon name="plus" class="me-1" />Add</span>
            </button>
          </div>
        </div>

        <!-- Membership management (edit mode only) -->
        <div v-if="editor.id" class="memberships mt-4">
          <h6 class="small text-muted text-uppercase mb-2">List memberships</h6>
          <table v-if="memberships.length" class="table table-sm align-middle mb-2">
            <thead>
              <tr><th>List</th><th>Status</th><th class="text-end">&nbsp;</th></tr>
            </thead>
            <tbody>
              <tr v-for="m in memberships" :key="m.list_id">
                <td>{{ m.list_name || `#${m.list_id}` }}</td>
                <td>
                  <span :class="['badge', m.status === 'subscribed' ? 'bg-success' : 'bg-secondary']">
                    {{ m.status }}
                  </span>
                </td>
                <td class="text-end">
                  <button
                    v-if="m.status === 'subscribed'"
                    class="btn btn-link btn-sm text-danger p-0"
                    :disabled="busy.member"
                    @click="removeFromList(m.list_id)"
                  >Remove</button>
                </td>
              </tr>
            </tbody>
          </table>
          <p v-else class="small text-muted mb-2">Not on any list yet.</p>

          <div class="d-flex gap-2 align-items-end">
            <div>
              <label class="form-label small text-muted">Add to list</label>
              <select v-model.number="addListId" class="form-select form-select-sm">
                <option :value="null">— pick a list —</option>
                <option v-for="l in lists" :key="l.id" :value="l.id">{{ l.name }} (#{{ l.id }})</option>
              </select>
            </div>
            <button class="btn btn-outline-primary btn-sm" :disabled="!addListId || busy.member" @click="addToList">
              <CygnusIcon name="spinner" :spin="true" v-if="busy.member" />
              <span v-else><CygnusIcon name="plus" class="me-1" />Add</span>
            </button>
          </div>
        </div>
      </div>
    </section>

    <MusubiTabulator
      :rows="rows"
      :columns="columns"
      storage-key="admin.subscribers"
      :initial-sort="{ column: 'created', dir: 'desc' }"
      :page-size="25"
      :loading="busy.load"
      :error="error"
      @row-click="onRowClick"
    />

    <p v-if="lastMessage" class="actions-bar__msg mt-2 small">{{ lastMessage }}</p>

    <!-- Per-contact compose (follow-up-pool slice A) — "Send email" to the open subscriber. -->
    <MusubiComposeModal :open="composeOpen" :context="composeContext" @close="composeOpen = false" @sent="onComposed" />
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";
import MusubiTabulator from "@root/components/MusubiTabulator.vue";
import { esc } from "./util/cellHtml";

export default {
  name: 'AdminSubscribers',
  components: { MusubiTabulator },
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      rows: [],
      lists: [],          // for the add-to-list dropdowns
      memberships: [],    // current subscriber's list rows (edit mode)
      addListId: null,
      tagTaxonomyId: null,  // id of the `subscriber_tag` taxonomy
      availableTags: [],    // [{id,key,label}] — full term catalogue
      assignedTagIds: [],   // term ids assigned to the open subscriber
      tagInput: '',
      busy: { load: false, save: false, member: false, tag: false },
      error: null,
      lastMessage: '',
      editor: null,
      composeOpen: false,
      composeContext: {},
    };
  },
  computed: {
    assignedTags() {
      const byId = new Map(this.availableTags.map((t) => [t.id, t]));
      return this.assignedTagIds.map((id) => byId.get(id)).filter(Boolean);
    },
    unassignedTags() {
      const assigned = new Set(this.assignedTagIds);
      return this.availableTags.filter((t) => !assigned.has(t.id));
    },
    isErased() {
      return !!(this.editor && this.editor.id && this.editor.erased_at);
    },
    columns() {
      return [
        { key: 'id',      label: 'ID',      width: 60,  sorter: 'number' },
        { key: 'email',   label: 'Email' },
        { key: 'name',    label: 'Name',    format: (v) => esc(v) || '—' },
        { key: 'status',  label: 'Status',  width: 120,
          format: (v, row) => {
            const label = esc(v) || '—';
            return row.erased_at
              ? `${label} <span class="badge bg-warning text-dark ms-1" title="Erased (GDPR right-to-erasure)">erased</span>`
              : label;
          } },
        { key: 'basis',   label: 'Basis',   width: 110,
          format: (v) => {
            if (!v) return '<span class="text-muted">—</span>';
            const legit = v === 'legitimate_interest';
            return `<span class="badge ${legit ? 'bg-info text-dark' : 'bg-success'}">${legit ? 'Legit. interest' : 'Consent'}</span>`;
          } },
        { key: 'source',  label: 'Source',  width: 120, format: (v) => esc(v) || '—' },
        { key: 'created', label: 'Created', width: 130, sorter: 'number',
          format: (v) => (v ? new Date(v * 1000).toLocaleDateString() : '—') },
      ];
    },
  },
  mounted() {
    this.refresh();
    this.loadLists();
  },
  methods: {
    formatDate(v) {
      return v ? new Date(v * 1000).toLocaleDateString() : '—';
    },
    // Compose one email to the open subscriber (through the standard machinery).
    composeToSubscriber() {
      if (!this.editor?.id) return;
      this.composeContext = {
        to_email: this.editor.email,
        to_name: this.editor.name || [this.editor.first_name, this.editor.last_name].filter(Boolean).join(' '),
        subscriber_id: this.editor.id,
        subject: '',
      };
      this.composeOpen = true;
    },
    onComposed() {
      this.lastMessage = 'Queued — the email will send on the next queue tick.';
    },
    async refresh() {
      this.busy.load = true; this.error = null;
      try {
        const res = await this.api.get('/api/v1/admin/subscribers');
        const payload = res?.data?.data ?? res?.data ?? res;
        this.rows = Array.isArray(payload) ? payload : (payload?.rows || []);
      } catch (e) { this.error = e?.message || 'Failed to load subscribers.'; }
      this.busy.load = false;
    },
    async loadLists() {
      try {
        const res = await this.api.get('/api/v1/admin/lists');
        const payload = res?.data?.data ?? res?.data ?? res;
        this.lists = Array.isArray(payload) ? payload : (payload?.rows || []);
      } catch (e) { /* non-fatal — add-to-list just won't have options */ }
    },
    openCreate() {
      this.editor = { email: '', first_name: '', last_name: '', name: '', status: 'active', source: '', list_id: null, basis: 'legitimate_interest' };
      this.memberships = [];
      this.addListId = null;
      this.lastMessage = '';
    },
    closeEditor() {
      this.editor = null;
      this.memberships = [];
      this.addListId = null;
      this.resetTags();
    },
    resetTags() {
      this.tagTaxonomyId = null;
      this.availableTags = [];
      this.assignedTagIds = [];
      this.tagInput = '';
    },
    async onRowClick(row) {
      if (!row?.id) return;
      try {
        const res = await this.api.get(`/api/v1/admin/subscribers/${row.id}`);
        const payload = res?.data ?? res;
        const sub = payload?.subscriber;
        if (!sub) return;
        this.editor = {
          id: sub.id,
          email: sub.email || '',
          first_name: sub.first_name || '',
          last_name: sub.last_name || '',
          name: sub.name || '',
          status: sub.status || 'active',
          source: sub.source || '',
          basis: sub.basis || null,
          erased_at: sub.erased_at || null,
          replied_at: sub.replied_at || null,
          bounced_at: sub.bounced_at || null,
          complained_at: sub.complained_at || null,
        };
        this.memberships = Array.isArray(payload.lists) ? payload.lists : [];
        this.addListId = null;
        this.lastMessage = '';
        this.loadTags(sub.id);
      } catch (e) { this.error = e?.message || 'Failed to open subscriber.'; }
    },
    async loadTags(subId) {
      this.resetTags();
      try {
        const res = await this.api.get(`/api/v1/entity-tags/subscriber/${subId}`);
        const payload = res?.data ?? res;
        const tax = (payload?.taxonomies || []).find((t) => t.key === 'subscriber_tag');
        if (!tax) return; // taxonomy not synced yet — fail soft
        this.tagTaxonomyId = tax.id;
        this.availableTags = (tax.available_terms || []).map((t) => ({ id: t.id, key: t.key, label: t.label }));
        this.assignedTagIds = Array.isArray(tax.assigned_term_ids) ? tax.assigned_term_ids.slice() : [];
      } catch (e) { /* non-fatal — tag editor just stays empty */ }
    },
    /** Snake_case slug for a new term key (createTerm requires ^[a-z][a-z0-9_]*$). */
    tagSlug(text) {
      let s = String(text).toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
      if (!/^[a-z]/.test(s)) s = 't_' + s;
      return s.slice(0, 60) || 't_tag';
    },
    async addTag() {
      const label = this.tagInput.trim();
      if (!label || !this.editor?.id || !this.tagTaxonomyId) return;
      this.busy.tag = true; this.error = null;
      try {
        const key = this.tagSlug(label);
        // Reuse an existing term (match on key or label, case-insensitive), else create one.
        let term = this.availableTags.find(
          (t) => t.key === key || t.label.toLowerCase() === label.toLowerCase()
        );
        if (!term) {
          const res = await this.api.post(`/api/v1/admin/taxonomies/${this.tagTaxonomyId}/terms`, { key, label });
          const payload = res?.data ?? res;
          const created = payload?.term ?? payload;
          if (!created?.id) { this.error = payload?.error || 'Could not create tag.'; this.busy.tag = false; return; }
          term = { id: created.id, key: created.key || key, label: created.label || label };
          this.availableTags.push(term);
        }
        if (!this.assignedTagIds.includes(term.id)) {
          await this.putTags([...this.assignedTagIds, term.id]);
        }
        this.tagInput = '';
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Add tag failed.';
      }
      this.busy.tag = false;
    },
    async removeTag(termId) {
      if (!this.editor?.id || !this.tagTaxonomyId) return;
      this.busy.tag = true; this.error = null;
      try {
        await this.putTags(this.assignedTagIds.filter((id) => id !== termId));
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Remove tag failed.';
      }
      this.busy.tag = false;
    },
    /** Replace the subscriber's full subscriber_tag assignment; sync from the echo. */
    async putTags(termIds) {
      const res = await this.api.put(
        `/api/v1/entity-tags/subscriber/${this.editor.id}/subscriber_tag`,
        { term_ids: termIds }
      );
      const payload = res?.data ?? res;
      this.assignedTagIds = Array.isArray(payload?.assigned_term_ids) ? payload.assigned_term_ids : termIds;
    },
    async saveSubscriber() {
      this.busy.save = true; this.error = null;
      try {
        const body = {
          email: this.editor.email,
          first_name: this.editor.first_name || null,
          last_name: this.editor.last_name || null,
          name: this.editor.name || null,
          status: this.editor.status,
          source: this.editor.source || null,
        };
        if (this.editor.id) {
          const res = await this.api.put(`/api/v1/admin/subscribers/${this.editor.id}`, body);
          const payload = res?.data ?? res;
          this.memberships = Array.isArray(payload?.lists) ? payload.lists : this.memberships;
          this.lastMessage = `Subscriber #${this.editor.id} updated.`;
        } else {
          if (this.editor.list_id) body.list_id = this.editor.list_id;
          // Always state the basis: the server records legitimate_interest when it is missing.
          body.basis = this.editor.basis === 'consent' ? 'consent' : 'legitimate_interest';
          const res = await this.api.post('/api/v1/admin/subscribers', body);
          const payload = res?.data ?? res;
          if (payload?.subscriber?.id) {
            this.editor.id = payload.subscriber.id;
            this.memberships = Array.isArray(payload.lists) ? payload.lists : [];
            this.lastMessage = `Subscriber #${this.editor.id} created.`;
          } else {
            this.error = payload?.error || 'Create failed.';
          }
        }
        await this.refresh();
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Save failed.';
      }
      this.busy.save = false;
    },
    async addToList() {
      if (!this.addListId || !this.editor?.id) return;
      this.busy.member = true; this.error = null;
      try {
        const res = await this.api.post(`/api/v1/admin/subscribers/${this.editor.id}/lists`, { list_id: this.addListId });
        const payload = res?.data ?? res;
        this.memberships = Array.isArray(payload?.lists) ? payload.lists : this.memberships;
        this.addListId = null;
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Add to list failed.';
      }
      this.busy.member = false;
    },
    async removeFromList(listId) {
      if (!this.editor?.id) return;
      this.busy.member = true; this.error = null;
      try {
        const res = await this.api.delete(`/api/v1/admin/subscribers/${this.editor.id}/lists/${listId}`);
        const payload = res?.data ?? res;
        this.memberships = Array.isArray(payload?.lists) ? payload.lists : this.memberships;
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Remove failed.';
      }
      this.busy.member = false;
    },
    async deleteSubscriber(id) {
      if (!confirm(`Delete subscriber #${id}? Blocked if they have email history — unsubscribe instead.`)) return;
      this.error = null;
      try {
        await this.api.delete(`/api/v1/admin/subscribers/${id}`);
        this.closeEditor();
        this.lastMessage = `Subscriber #${id} deleted.`;
        await this.refresh();
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Delete failed.';
      }
    },
  },
};
</script>

<style>
.musubi-page--subscribers { padding: var(--m-space-6, 32px); }
.musubi-page--subscribers .actions-bar { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }
.musubi-page--subscribers .actions-bar__msg { color: var(--m-text-secondary, #475569); }
.musubi-page--subscribers .editor { background: var(--m-bg-card, #FFF); }
.musubi-page--subscribers .memberships { border-top: 1px solid var(--m-border, #E2E8F0); padding-top: 14px; }
.musubi-page--subscribers .tags { border-top: 1px solid var(--m-border, #E2E8F0); padding-top: 14px; }
.musubi-page--subscribers .compliance { border-top: 1px solid var(--m-border, #E2E8F0); padding-top: 14px; }
.musubi-page--subscribers .compliance__grid {
  display: grid; grid-template-columns: auto 1fr; gap: 4px 12px; align-items: center;
}
.musubi-page--subscribers .compliance__grid dt { color: var(--m-text-secondary, #475569); }
.musubi-page--subscribers .compliance__grid dd { margin: 0; }
.musubi-page--subscribers .tag-chips { display: flex; flex-wrap: wrap; gap: 6px; min-height: 24px; }
.musubi-page--subscribers .tag-chip {
  display: inline-flex; align-items: center; gap: 6px;
  background: var(--m-bg-subtle, #EEF2FF); color: var(--m-text-primary, #0F172A);
  border: 1px solid var(--m-border, #C7D2FE); border-radius: 999px;
  padding: 2px 6px 2px 10px; font-size: 0.8rem; line-height: 1.4;
}
.musubi-page--subscribers .tag-chip__x {
  border: 0; background: transparent; cursor: pointer; color: var(--m-text-secondary, #475569);
  font-size: 1rem; line-height: 1; padding: 0 2px;
}
.musubi-page--subscribers .tag-chip__x:hover { color: var(--m-danger, #DC2626); }
</style>
